import { Reveal, MaskLines, ImageReveal, VideoWithSound } from "../components/motion/Reveal.jsx";
import SectionHeading from "../components/ui/SectionHeading.jsx";
import CTABanner from "../components/ui/CTABanner.jsx";
import TestimonialCard from "../components/cards/TestimonialCard.jsx";
import useSeo from "../hooks/useSeo.js";
import { TESTIMONIALS, ENRICHED } from "../content.js";

const GENERATIONS = ["Kids", "Adults", "Families", "Athletes"];
const ECOSYSTEM = ["Fitness", "Sports", "Wellness", "Recovery", "Community"];

export default function About() {
  useSeo({
    title: "About / Our Story | Founded 2019 — Oneiros Sports Club Surat",
    description:
      "Founded in 2019, Oneiros Sports Club was built with a bigger purpose — to redefine what a sports club can offer. Discover the story, purpose and ecosystem behind Surat's premium sports club.",
    image: "/oneiros/strongest-gym.jpg",
  });
  return (
    <>
      <section className="mx-auto max-w-[1440px] px-6 pb-14 pt-36 lg:px-12 lg:pt-48">
        <SectionHeading
          eyebrow="About / Our Story"
          lines={["More Than a Club.", "A Movement."]}
          copy="Founded in 2019, Oneiros Sports Club was built with a bigger purpose — to redefine what a sports club can offer."
        />
        <div className="mt-14 grid gap-5 lg:grid-cols-12">
          <ImageReveal src="/oneiros/katargam-ext.jpg" alt="Oneiros Sports Club Katargam — current building" className="lg:col-span-7 aspect-[16/10] w-full" />
          <div className="grid gap-5 lg:col-span-5">
            <ImageReveal src="/oneiros/adajan-ext.jpg" alt="Oneiros Sports Club Adajan — current building" className="aspect-[16/10] w-full" delay={0.08} />
            <ImageReveal src="/oneiros/varachha-outer.png" alt="Oneiros Sports Club Varachha — new destination" className="aspect-[16/10] w-full" delay={0.16} />
          </div>
        </div>
        <Reveal delay={0.2}>
          <p className="mt-6 text-center text-[10px] font-bold uppercase tracking-[0.3em] text-faint">
            Katargam · Adajan · Varachha — one Oneiros standard across Surat
          </p>
        </Reveal>
      </section>

      <section className="border-y border-hairline bg-mist py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <SectionHeading eyebrow="Who We Are" lines={["Sports. Fitness.", "Family. Together."]} copy="Oneiros is a complete sports and wellness destination — a world-class ecosystem for athletes, swimmers, fitness enthusiasts and future sports champions, with premium clubs across Surat." />
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {[
              ["01", "A Complete Destination", "High-performance gym, fitness studios, indoor and outdoor sports, family zones and recovery — every facility built for strength, movement, recreation and togetherness."],
              ["02", "Every Generation", "From kids discovering their sport to adults training with purpose — the club brings everyone together under one roof."],
              ["03", "A Bigger Purpose", "To redefine what a sports club can truly offer — and build a stronger, healthier, happier Surat."],
            ].map(([n, t, c], i) => (
              <Reveal key={n} delay={i * 0.08}>
                <div className="flex h-full flex-col gap-4 border-t border-navy/15 pt-6">
                  <p className="ghost-number font-display text-6xl">{n}</p>
                  <p className="font-display text-2xl text-navy">{t}</p>
                  <p className="text-sm leading-relaxed text-mute">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="grain bg-navy-obsidian py-24 text-white lg:py-32">
        <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
          <div>
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-[2px] w-8 bg-brand" /> Our Purpose
              </p>
            </Reveal>
            <MaskLines lines={["A Stronger,", "Healthier,", "Happier Surat."]} className="mt-6 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl" />
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-white/70">{ENRICHED.aspiration}</p>
            </Reveal>
          </div>
          <ImageReveal src="/oneiros/campaign-5l.jpg" alt="A stronger Surat — Oneiros mission" className="aspect-[4/3] w-full" />
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <SectionHeading eyebrow="Built for Performance" lines={["World-Class", "Strength & Conditioning."]} />
        <div className="mt-14 grid items-center gap-12 lg:grid-cols-2">
          <div className="grid gap-4 sm:grid-cols-2">
            {["Asia's Strongest Gym", "300 Kg Lift Story", "Professional Guidance", "International-Standard Equipment"].map((p, i) => (
              <Reveal key={p} delay={i * 0.07}>
                <div data-testid={`about-performance-${i}`} className="flex h-full flex-col justify-between gap-6 rounded-xl border border-hairline bg-white p-7 transition-colors hover:border-brand/50">
                  <span className="font-display text-2xl text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm font-bold uppercase tracking-[0.15em] text-navy">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <ImageReveal src="/oneiros/strongest-gym.jpg" alt="Asia's Strongest Gym — Oneiros strength & conditioning" className="aspect-[4/3] w-full" />
        </div>
      </section>

      <section className="border-y border-hairline bg-mist py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <SectionHeading eyebrow="Every Generation" lines={["Built for", "Every Generation."]} />
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
            {GENERATIONS.map((g, i) => (
              <Reveal key={g} delay={i * 0.06}>
                <div className="border border-hairline bg-white px-6 py-10 text-center transition-colors hover:border-brand/50">
                  <p className="font-display text-3xl text-navy">{g}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <SectionHeading eyebrow="The Ecosystem" lines={["The Oneiros", "Ecosystem."]} />
        <div className="mt-12 flex flex-col">
          {ECOSYSTEM.map((e, i) => (
            <Reveal key={e} delay={i * 0.05}>
              <div data-testid={`about-ecosystem-${e.toLowerCase()}`} className="group flex items-center justify-between border-t border-hairline py-6 last:border-b">
                <span className="font-display text-3xl text-navy transition-colors group-hover:text-brand md:text-5xl">{e}</span>
                <span className="font-display text-xl text-faint">{String(i + 1).padStart(2, "0")}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="grain bg-navy py-24 text-white lg:py-32">
        <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
          <div>
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-[2px] w-8 bg-brand" /> From Commitment to Transformation
              </p>
            </Reveal>
            <MaskLines lines={["From \u201COne Day\u201D", "to \u201CDay One\u201D."]} className="mt-6 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl" />
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-white/70">{ENRICHED.transformation}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.25em] text-brand">Jesus Mehta — Oneiros Member Transformation</p>
            </Reveal>
          </div>
          <VideoWithSound src="/oneiros/transformation.mp4" poster="/oneiros/poster-transformation.jpg" testId="transformation-video" className="aspect-[9/16] w-full max-w-[440px] lg:justify-self-end" />
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <SectionHeading eyebrow="Member Voices" lines={["Proven by", "Customer Experiences."]} />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.slice(0, 2).map((t) => (
            <TestimonialCard key={t.id} testimonial={t} />
          ))}
        </div>
      </section>

      <CTABanner
        eyebrow="About Oneiros"
        lines={["A Stronger Tomorrow", "Starts Today."]}
        primary={{ label: "Enquire Now", to: "/contact" }}
        secondary={{ label: "Visit a Club", to: "/our-clubs" }}
        testId="about-final-cta"
      />
    </>
  );
}
